import React, { useState } from "react";
import "./HomePage.css";
import { updateCompany } from "../../../api/add-company";

type Panel = {
  watt: string;
  voc: number;
  isc: number;
  price: number;
  name: string;
  efficiency: number;
};

type Inverter = {
  watt: string;
  name: string;
  price: number;
  efficiency: number;
  maxInputVoltage: number;
  minInputVoltage: number;
  maxInputCurrent: number;
  type?: string;
  phase?: string;
};

type Battery = {
  batteryName: string;
  batteryPower: number;
  batteryPrice: number;
  betteryAh: number;
  batteryVoltage: number;
  batteryType: string;
  batteryCycle: number;
};

type OtherCost = {
  costName: string;
  costValue: number;
};

type FormData = {
  panel: Panel[];
  inverter: Inverter[];
  battery: Battery[];
  otherCost: OtherCost[];
  phoneNumber: string;
  address: string;
};

const initialFormData: FormData = {
  panel: [],
  inverter: [],
  battery: [],
  otherCost: [],
  phoneNumber: "",
  address: "",
};

type HomePageProps = {
  companyInfo: any; // Replace 'any' with the actual type if available
};

const HomePage: React.FC<HomePageProps> = ({ companyInfo }) => {
  const [formData, setFormData] = useState<FormData>(
    companyInfo[0] || initialFormData
  );
  const [modalType, setModalType] = useState<
    "panel" | "inverter" | "battery" | "otherCost" | null
  >(null);
  const [modalData, setModalData] = useState<any>({});
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleOpenModal = (
    type: "panel" | "inverter" | "battery" | "otherCost",
    index: number | null = null
  ) => {
    setModalType(type);
    setEditIndex(index);
    setModalData(index !== null ? formData[type][index] : {});
    setShowModal(true);
  };

  const handleModalChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setModalData((prev: any) => ({
      ...prev,
      [name]: isNaN(Number(value)) ? value : parseFloat(value),
    }));
  };

  const handleModalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (modalType) {
      setFormData((prev) => {
        const updatedArray = [...prev[modalType]];
        if (editIndex !== null) {
          updatedArray[editIndex] = modalData;
        } else {
          updatedArray.push(modalData);
        }
        return {
          ...prev,
          [modalType]: updatedArray,
        };
      });
    }
    setShowModal(false);
    setEditIndex(null);
  };

  const handleDelete = (
    type: "panel" | "inverter" | "battery" | "otherCost",
    index: number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [type]: (prev[type] as any[]).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateCompany(companyInfo[0]._id, {
        ...companyInfo,
        ...formData,
      });
      alert("Data saved successfully!");
    } catch (error) {
      console.error("Error submitting form data:", error);
      alert("An error occurred while submitting the form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h1>املأ معلومات النظام الشمسي</h1>
      <form
        onSubmit={handleSubmit}
        className="solar-form"
        aria-label="Solar System Form">
        {/* Panel Section */}
        <div className="form-section">
          <p>الألواح الشمسية</p>
          <button
            type="button"
            className="add-button"
            aria-label="إضافة لوح شمسي"
            onClick={() => handleOpenModal("panel")}>
            +
          </button>
          <ul>
            {formData.panel.map((item, index) => (
              <li key={index}>
                {item.name || `Panel ${index + 1}`}
                <button
                  type="button"
                  onClick={() => handleOpenModal("panel", index)}
                  className="edit-button"
                  aria-label={`تعديل لوح شمسي ${index + 1}`}>
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete("panel", index)}
                  className="delete-button"
                  aria-label={`حذف لوح شمسي ${index + 1}`}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Inverter Section */}
        <div className="form-section">
          <p>العاكسات</p>
          <button
            type="button"
            className="add-button"
            aria-label="إضافة عاكس"
            onClick={() => handleOpenModal("inverter")}>
            +
          </button>
          <ul>
            {formData.inverter.map((item, index) => (
              <li key={index}>
                {item.name || `Inverter ${index + 1}`}
                <button
                  type="button"
                  onClick={() => handleOpenModal("inverter", index)}
                  className="edit-button"
                  aria-label={`تعديل عاكس ${index + 1}`}>
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete("inverter", index)}
                  className="delete-button"
                  aria-label={`حذف عاكس ${index + 1}`}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Battery Section */}
        <div className="form-section">
          <p>بطاريات</p>
          <button
            type="button"
            className="add-button"
            aria-label="إضافة بطارية"
            onClick={() => handleOpenModal("battery")}>
            +
          </button>
          <ul>
            {formData.battery.map((item, index) => (
              <li key={index}>
                {item.batteryName || `Battery ${index + 1}`}
                <button
                  type="button"
                  onClick={() => handleOpenModal("battery", index)}
                  className="edit-button"
                  aria-label={`تعديل بطارية ${index + 1}`}>
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete("battery", index)}
                  className="delete-button"
                  aria-label={`حذف بطارية ${index + 1}`}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Other Costs Section */}
        <div className="form-section">
          <p> تكاليف أخرى</p>
          <button
            type="button"
            className="add-button"
            aria-label="إضافة تكلفة أخرى"
            onClick={() => handleOpenModal("otherCost")}>
            +
          </button>
          <ul>
            {formData.otherCost.map((item, index) => (
              <li key={index}>
                {item.costName || `Other Cost ${index + 1}`}
                <button
                  type="button"
                  onClick={() => handleOpenModal("otherCost", index)}
                  className="edit-button"
                  aria-label={`تعديل تكلفة أخرى ${index + 1}`}>
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete("otherCost", index)}
                  className="delete-button"
                  aria-label={`حذف تكلفة أخرى ${index + 1}`}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Information */}
        <fieldset>
          <legend>معلومات الاتصال</legend>
          <input
            type="text"
            name="phoneNumber"
            placeholder="Phone Number"
            value={formData.phoneNumber}
            onChange={(e) =>
              setFormData({ ...formData, phoneNumber: e.target.value })
            }
            aria-label="Phone Number"
          />
          <textarea
            name="address"
            placeholder="Address"
            value={formData.address}
            onChange={(e) =>
              setFormData({ ...formData, address: e.target.value })
            }
            aria-label="Address"></textarea>
        </fieldset>

        <button
          type="submit"
          className="submit-button"
          disabled={loading}
          aria-busy={loading}>
          {loading ? "Saving..." : "Submit"}
        </button>
      </form>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <h2>
              {editIndex !== null ? `Edit ${modalType}` : `Add ${modalType}`}
            </h2>
            <form onSubmit={handleModalSubmit}>
              {modalType === "panel" && (
                <>
                  <input
                    type="text"
                    name="watt"
                    placeholder="Watt"
                    value={modalData.watt || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="voc"
                    placeholder="VOC"
                    value={modalData.voc || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="isc"
                    placeholder="ISC"
                    value={modalData.isc || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="price"
                    placeholder="Price"
                    value={modalData.price || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={modalData.name || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="efficiency"
                    placeholder="Efficiency"
                    value={modalData.efficiency || ""}
                    onChange={handleModalChange}
                  />
                </>
              )}
              {modalType === "inverter" && (
                <>
                  <input
                    type="text"
                    name="watt"
                    placeholder="Watt"
                    value={modalData.watt || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={modalData.name || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="price"
                    placeholder="Price"
                    value={modalData.price || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="efficiency"
                    placeholder="Efficiency"
                    value={modalData.efficiency || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="maxInputVoltage"
                    placeholder="Max Input Voltage"
                    value={modalData.maxInputVoltage || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="minInputVoltage"
                    placeholder="Min Input Voltage"
                    value={modalData.minInputVoltage || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="maxInputCurrent"
                    placeholder="Max Input Current"
                    value={modalData.maxInputCurrent || ""}
                    onChange={handleModalChange}
                  />
                  <select
                    name="phase"
                    value={modalData.phase || ""}
                    onChange={handleModalChange}>
                    <option value="">Select Phase</option>
                    <option value="single">Single Phase</option>
                    <option value="three">Three Phase</option>
                  </select>
                  <select
                    name="type"
                    value={modalData.type || ""}
                    onChange={handleModalChange}>
                    <option value="">Select Type</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="ongrid">OnGrid</option>
                    <option value="offgrid">OffGrid</option>
                  </select>
                </>
              )}
              {modalType === "battery" && (
                <>
                  <input
                    type="text"
                    name="batteryName"
                    placeholder="Battery Name"
                    value={modalData.batteryName || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="batteryPower"
                    placeholder="Battery Power"
                    value={modalData.batteryPower || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="batteryPrice"
                    placeholder="Battery Price"
                    value={modalData.batteryPrice || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="betteryAh"
                    placeholder="Battery Ah"
                    value={modalData.betteryAh || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="batteryVoltage"
                    placeholder="Battery Voltage"
                    value={modalData.batteryVoltage || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="text"
                    name="batteryType"
                    placeholder="Battery Type"
                    value={modalData.batteryType || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="batteryCycle"
                    placeholder="Battery Cycle"
                    value={modalData.batteryCycle || ""}
                    onChange={handleModalChange}
                  />
                </>
              )}
              {modalType === "otherCost" && (
                <>
                  <input
                    type="text"
                    name="costName"
                    placeholder="Cost Name"
                    value={modalData.costName || ""}
                    onChange={handleModalChange}
                  />
                  <input
                    type="number"
                    name="costValue"
                    placeholder="Cost Value"
                    value={modalData.costValue || ""}
                    onChange={handleModalChange}
                  />
                </>
              )}
              <button type="submit" className="submit-button">
                {editIndex !== null ? "Update" : "Add"}
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="close-button">
                Close
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
